/**
 * How long an exploratory run may work, chosen at start. Only flavors that
 * declare an `effort` capability (the fuzzer today) take one; the API start
 * route, the run's budget and the web launcher all read this one list.
 */
export const EXPLORATORY_EFFORT_VALUES = ['quick', 'standard', 'deep'] as const;

export type ExploratoryEffort = (typeof EXPLORATORY_EFFORT_VALUES)[number];

/** The run's time budget: about 5, 15 and 30 minutes. */
export const EXPLORATORY_EFFORT_MINUTES: Record<ExploratoryEffort, number> = {
  quick: 5,
  standard: 15,
  deep: 30,
};

/** What the fuzzer found and covered, stored as the run's `reportJson`. */
export type FuzzReportJson = {
  kind: 'fuzz';
  effort: ExploratoryEffort;
  minutesUsed: number;
  /**
   * Why the run ended: it worked through its plan and consecutive attacks stopped
   * turning up anything new (`saturated`), it used its whole budget (`budget_used`),
   * the agent finished a quick run on its own (`agent_finished`), or it ended
   * without a final report and the report was built from its state (`no_report`).
   */
  stopReason: 'saturated' | 'budget_used' | 'agent_finished' | 'no_report';
  summary: string;
  /** Every area the fuzzer planned to attack, in the order it was added, with its result. */
  attackSurface: Array<{
    id: string;
    area: string;
    status: 'untried' | 'clean' | 'bug_found' | 'blocked';
    result: string | null;
  }>;
  /** Reproduced bugs filed as issues. */
  findings: {
    total: number;
    byCategory: Record<string, number>;
    bySeverity: { low: number; medium: number; high: number };
  };
  /** Private environments created and browser sessions opened. */
  environmentsUsed: number;
  sessionsUsed: number;
  /** What the fuzzer did not get to, or could not attack, in its own words. */
  coverageGaps: string[];
};
